import { Link } from "react-router-dom";
import { ArrowRight, Camera, Check, MessageCircle, Phone, Quote, Star } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SERVICES } from "@/data/services";
import { REVIEWS, RIGHTS } from "@/data/content";
import { KOELN, UMLAND } from "@/data/areas";
import { ServiceIcon } from "@/lib/icons";
import { SITE } from "@/lib/site";

export const SectionHeading = ({ eyebrow, title, intro }) => (
  <div className="mb-8 max-w-2xl">
    {eyebrow && <p className="eyebrow mb-3">{eyebrow}</p>}
    <h2 className="h2-brand text-2xl sm:text-3xl">{title}</h2>
    {intro && <p className="mt-3 text-base text-brand-ink">{intro}</p>}
  </div>
);

export const ServiceCards = () => (
  <div className="grid gap-4 sm:grid-cols-2" data-testid="service-cards">
    {SERVICES.map((s) => (
      <Link
        key={s.slug}
        to={`/${s.slug}`}
        data-testid={`service-card-${s.slug}`}
        className="group flex flex-col rounded-2xl border border-slate-200 bg-white p-6 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-brand-blue/30 hover:shadow-soft"
      >
        <span className="mb-4 grid h-12 w-12 place-items-center rounded-xl bg-brand-yellow text-brand-anthracite transition-transform duration-300 group-hover:scale-105">
          <ServiceIcon name={s.icon} className="h-6 w-6" />
        </span>
        <h3 className="text-lg font-bold text-brand-anthracite">{s.name}</h3>
        <p className="mt-2 flex-1 text-[15px] leading-relaxed text-brand-ink">{s.teaser}</p>
        <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-brand-blue">
          Mehr erfahren <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </span>
      </Link>
    ))}
    <Link
      to="/schaden-melden"
      data-testid="service-card-damage-cta"
      className="group flex flex-col justify-between rounded-2xl bg-brand-blue p-6 text-white transition-transform duration-300 hover:-translate-y-1"
    >
      <span className="grid h-12 w-12 place-items-center rounded-xl bg-white/15"><Camera className="h-6 w-6" /></span>
      <span>
        <span className="mt-4 block text-lg font-bold">Schaden direkt melden</span>
        <span className="mt-2 block text-[15px] text-white/85">Mit Fotos vom Handy – wir melden uns meist innerhalb von 30 Minuten.</span>
      </span>
      <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-brand-yellow">Jetzt starten <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
    </Link>
  </div>
);

export const Steps = ({ items, prefix = "steps" }) => (
  <ol className="grid gap-4 sm:grid-cols-2" data-testid={prefix}>
    {items.map((s, i) => (
      <li key={s.title} className="relative rounded-2xl bg-brand-mist p-6">
        <span className="mb-4 grid h-11 w-11 place-items-center rounded-full bg-brand-yellow text-[15px] font-bold text-brand-anthracite">
          {String(i + 1).padStart(2, "0")}
        </span>
        <h3 className="text-lg font-bold text-brand-anthracite">{s.title}</h3>
        <p className="mt-2 text-[15px] leading-relaxed text-brand-ink">{s.text}</p>
      </li>
    ))}
  </ol>
);

export const Rights = () => (
  <div className="overflow-hidden rounded-3xl bg-brand-blue p-7 text-white sm:p-10" data-testid="rights-section">
    <p className="eyebrow mb-3 !text-brand-yellow">Bitte beachten</p>
    <h2 className="text-2xl font-bold sm:text-3xl">Ihre 5 Rechte nach einem unverschuldeten Unfall</h2>
    <ol className="mt-8 space-y-4">
      {RIGHTS.map((r, i) => (
        <li key={r.title} className="flex gap-4 rounded-2xl bg-white/10 p-4 backdrop-blur-sm">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-brand-yellow font-bold text-brand-anthracite">{i + 1}</span>
          <span>
            <span className="block font-bold">{r.title}</span>
            <span className="block text-[15px] text-white/85">{r.text}</span>
          </span>
        </li>
      ))}
    </ol>
    <p className="mt-6 text-[15px] leading-relaxed text-white/85">
      Als Unfallopfer dürfen Sie einen Sachverständigen Ihrer Wahl mit der Beweissicherung und der Feststellung von Schadenumfang und
      Schadenhöhe beauftragen – auch dann, wenn die Versicherung ohne Ihre Zustimmung bereits einen eigenen Gutachter bestellt hat.
      Die Kosten trägt die Versicherung des Unfallverursachers.
    </p>
  </div>
);

export const ExpertBox = () => (
  <div className="grid gap-6 rounded-3xl border border-slate-200 bg-white p-6 sm:grid-cols-[240px_1fr] sm:p-8" data-testid="expert-box">
    <img
      src="/images/real-marcone-buero-800.webp"
      alt="Kfz-Sachverständiger Francesco Marcone in seinem Büro in Köln-Nippes"
      loading="lazy"
      width="800"
      height="536"
      className="h-64 w-full rounded-2xl object-cover object-[68%_30%] sm:h-full"
      data-testid="expert-photo"
    />
    <div>
      <p className="eyebrow mb-3">Ihr Sachverständiger</p>
      <h2 className="h2-brand text-2xl">Francesco Marcone – Ihr TÜV-Partner in Köln</h2>
      <p className="mt-3 text-[15px] leading-relaxed">
        Wir sind erfahren und kompetent im Schadenmanagement und sorgen dafür, dass Ihre Rechte gewahrt bleiben. Wir sprechen die Sprache
        unserer Kunden und erklären technische Sachverhalte ohne Fachvokabular – werkstatt- und versicherungsunabhängig, als Partner für Ihre Interessen.
      </p>
      <ul className="mt-4 grid gap-2 text-[15px] sm:grid-cols-2">
        {["TÜV-zertifiziert", "FSP-Partner", "Büro in Köln-Nippes", "Mo–Fr 24 h erreichbar"].map((t) => (
          <li key={t} className="flex items-center gap-2 font-semibold text-brand-anthracite"><Check className="h-4 w-4 text-brand-blue" />{t}</li>
        ))}
      </ul>
    </div>
  </div>
);

export const Reviews = () => (
  <div className="grid gap-4 md:grid-cols-3" data-testid="reviews">
    {REVIEWS.map((r, n) => (
      <figure key={r.name} className="relative flex flex-col rounded-2xl bg-white p-6 shadow-soft ring-1 ring-slate-100" data-testid={`review-${n + 1}`}>
        <Quote className="absolute right-5 top-5 h-8 w-8 text-brand-yellow" aria-hidden="true" />
        <div className="flex gap-0.5 text-brand-yellow" aria-label="5 von 5 Sternen">
          {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}
        </div>
        <blockquote className="mt-3 flex-1 text-[15px] leading-relaxed text-brand-ink">„{r.text}“</blockquote>
        <figcaption className="mt-5 flex items-center gap-3 border-t border-slate-100 pt-4">
          <span className="grid h-10 w-10 place-items-center rounded-full bg-brand-blue text-sm font-bold text-white">
            {r.name.split(" ").map((p) => p[0]).join("")}
          </span>
          <span className="leading-tight">
            <span className="block font-bold text-brand-anthracite">{r.name}</span>
            <span className="block text-xs text-slate-500">Kundenstimme</span>
          </span>
        </figcaption>
      </figure>
    ))}
  </div>
);

export const FaqAccordion = ({ items, prefix }) => (
  <Accordion type="single" collapsible className="space-y-3" data-testid={`${prefix}-faq`}>
    {items.map((it, i) => (
      <AccordionItem
        key={it.q}
        value={`q-${i}`}
        className="rounded-2xl border border-slate-200 bg-white px-5 transition-[border-color,box-shadow] data-[state=open]:border-brand-blue/40 data-[state=open]:shadow-soft"
      >
        <AccordionTrigger className="gap-4 py-4 text-left text-[15px] font-semibold text-brand-anthracite hover:no-underline" data-testid={`${prefix}-faq-q-${i}`}>
          {it.q}
        </AccordionTrigger>
        <AccordionContent className="text-[15px] leading-relaxed text-brand-ink">{it.a}</AccordionContent>
      </AccordionItem>
    ))}
  </Accordion>
);

const Chip = ({ to, children }) => (
  <li>
    <Link to={to} className="inline-block rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-sm font-semibold text-brand-anthracite transition-colors hover:border-brand-blue hover:text-brand-blue" data-testid={`area-chip${to.replace(/\//g, "-")}`}>
      {children}
    </Link>
  </li>
);

export const ServiceArea = () => (
  <div className="rounded-3xl bg-brand-mist p-6 sm:p-8" data-testid="service-area">
    <p className="text-[15px] leading-relaxed">
      Unser Büro liegt in Köln-Nippes an der Neusser Straße. Von hier aus sind wir schnell im gesamten Kölner Norden, in der Innenstadt
      und im Umland – und kommen direkt dorthin, wo Ihr Fahrzeug steht.
    </p>
    <p className="mb-3 mt-6 text-xs font-bold uppercase tracking-wider text-brand-blue">Köln</p>
    <ul className="flex flex-wrap gap-2">{KOELN.map((l) => <Chip key={l.slug} to={`/${l.slug}`}>{l.label}</Chip>)}</ul>
    <p className="mb-3 mt-6 text-xs font-bold uppercase tracking-wider text-brand-blue">Umland</p>
    <ul className="flex flex-wrap gap-2">{UMLAND.map((l) => <Chip key={l.slug} to={`/${l.slug}`}>{l.label}</Chip>)}</ul>
    <Link to="/einsatzgebiet" className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-brand-blue hover:underline" data-testid="service-area-overview-link">
      Zum gesamten Einsatzgebiet <ArrowRight className="h-4 w-4" />
    </Link>
  </div>
);

export const CtaBand = ({ prefix = "cta" }) => (
  <div className="flex flex-col gap-6 rounded-3xl bg-brand-anthracite p-7 text-white sm:p-10 md:flex-row md:items-center md:justify-between" data-testid={`${prefix}-band`}>
    <div>
      <p className="text-2xl font-bold">Unfall gehabt?</p>
      <p className="mt-1 text-white/75">In Köln sind wir meist innerhalb von 24 Stunden vor Ort.</p>
    </div>
    <div className="flex flex-wrap gap-3">
      <a href={SITE.phoneHref} className="inline-flex items-center gap-2 rounded-full bg-brand-yellow px-5 py-3 font-bold text-brand-anthracite transition-transform hover:-translate-y-0.5" data-testid={`${prefix}-call`}>
        <Phone className="h-4 w-4" /> {SITE.phone}
      </a>
      <a href={SITE.whatsappHref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-brand-whatsapp px-5 py-3 font-bold text-brand-anthracite transition-transform hover:-translate-y-0.5" data-testid={`${prefix}-whatsapp`}>
        <MessageCircle className="h-4 w-4" /> WhatsApp
      </a>
      <Link to="/schaden-melden" className="inline-flex items-center gap-2 rounded-full border border-white/30 px-5 py-3 font-bold text-white transition-colors hover:bg-white/10" data-testid={`${prefix}-damage`}>
        Schaden melden <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  </div>
);
